import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileAcceptType, UploadType } from '../../../../../hooks';
import { ImageBlockDataItem } from '../../../../../models';
import { IconLabel } from '../../../../IconLabel';
import { InputField } from '../../../../InputField';
import { CloseWrapper, MediaBlockUpload } from '../../helpers';

import classNames from 'classnames';
import classes from './ImageNodeView.module.scss';

const UPLOAD_LIMIT = 10;

export const ImageNodeView: FunctionComponent<NodeViewProps> = ({
  editor,
  node,
  getPos,
  updateAttributes,
}) => {
  const { t } = useTranslation();

  const items = node.attrs.items as ImageBlockDataItem[];

  const pastedFiles = node.attrs.pastedFiles as File[] | undefined;

  const [loading, setLoading] = useState<boolean>(false);

  const [selectId, setSelectId] = useState<number>(0);

  const [dragId, setDragId] = useState<number>(0);

  const isDraggable = useMemo(() => Boolean(!loading && items.length > 1), [items.length, loading]);

  const caption = useMemo(
    () => items.find((item) => item.image?.id === selectId)?.caption ?? '',
    [items, selectId]
  );

  const onDragStart = useCallback((id: number) => setDragId(id), []);

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    (event.target as HTMLDivElement).classList.add(classes['gallery__preview--ghost']);
  }, []);

  const onDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    (event.target as HTMLDivElement).classList.remove(classes['gallery__preview--ghost']);
  }, []);

  const onDragEnd = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    (event.target as HTMLDivElement).classList.remove(classes['gallery__preview--ghost']);
    setDragId(0);
  }, []);

  const onDrop = useCallback(
    ({ event, id }: { event: React.DragEvent<HTMLDivElement>; id: number }) => {
      event.preventDefault();

      (event.target as HTMLDivElement).classList.remove(classes['gallery__preview--ghost']);

      const itemsCopy = [...items];

      const dragIndex = itemsCopy.findIndex((item) => item.image?.id === dragId);

      if (dragIndex === -1) {
        setDragId(0);
        return;
      }

      const currentIndex = itemsCopy.findIndex((item) => item.image?.id === id);

      [itemsCopy[dragIndex], itemsCopy[currentIndex]] = [
        itemsCopy[currentIndex],
        itemsCopy[dragIndex],
      ];

      setDragId(0);

      updateAttributes({ items: itemsCopy });
    },
    [dragId, items, updateAttributes]
  );

  const removePreview = useCallback(
    (index: number) => {
      const itemsCopy = [...items];

      const { image } = itemsCopy.splice(index, 1)[0];

      if (selectId === image?.id) {
        setSelectId(0);
      }

      updateAttributes({ items: itemsCopy });
    },
    [items, selectId, updateAttributes]
  );

  const setCaption = useCallback(
    (caption: string) => {
      updateAttributes({
        items: items.map((item) => (item.image?.id === selectId ? { ...item, caption } : item)),
      });
    },
    [items, selectId, updateAttributes]
  );

  const preview = useMemo(() => {
    return (
      <div className={classes['gallery__preview-wrapper']}>
        {items.map(({ image, caption }, index) => {
          const { id = 0, url } = { ...image };

          return (
            <div
              key={id}
              draggable={isDraggable}
              onDragStart={() => onDragStart(id)}
              onDrop={(event) => onDrop({ event, id })}
              onDragEnd={onDragEnd}
              onDragLeave={onDragLeave}
              onDragOver={onDragOver}
              className={classNames(classes['gallery__preview'], {
                [classes['gallery__preview--loading']]: loading,
                [classes['gallery__preview--move']]: isDraggable,
                [classes['gallery__preview--dragging']]: dragId,
              })}
            >
              <div
                className={classes['gallery__preview-img']}
                style={{ backgroundImage: `url(${url})` }}
              ></div>
              <div className={classes['gallery__preview-actions']}>
                <span
                  className={classNames(classes['gallery__preview-actions-settings'], {
                    [classes['gallery__preview-actions-settings--active']]: id === selectId,
                  })}
                  onClick={() => setSelectId(selectId === id ? 0 : id)}
                >
                  <IconLabel iconId={caption ? 'pencil' : 'dots-menu'} iconSize={30} />
                </span>
                <span
                  className={classes['gallery__preview-actions-remove']}
                  onClick={() => removePreview(index)}
                ></span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [
    dragId,
    isDraggable,
    items,
    loading,
    onDragEnd,
    onDragLeave,
    onDragOver,
    onDragStart,
    onDrop,
    removePreview,
    selectId,
  ]);

  const content = useMemo(() => {
    return (
      <div className={classes['gallery-wrapper']}>
        {Boolean(items.length < UPLOAD_LIMIT) && (
          <MediaBlockUpload
            items={items}
            setItems={(items) => updateAttributes({ items })}
            pastedFiles={pastedFiles}
            loading={loading}
            setLoading={setLoading}
            fileAcceptType={FileAcceptType.IMAGE}
            uploadType={UploadType.IMAGE}
            uploadLimit={UPLOAD_LIMIT}
            multiple
          />
        )}
        {Boolean(items.length) && (
          <div className={classes['gallery']}>
            {preview}
            {Boolean(selectId) && (
              <InputField
                value={caption}
                onChange={({ target }) => setCaption(target.value)}
                label={t('richTextEditor.tools-image-caption')}
              />
            )}
          </div>
        )}
      </div>
    );
  }, [caption, items, loading, pastedFiles, preview, selectId, setCaption, t, updateAttributes]);

  return (
    <NodeViewWrapper as={'div'}>
      <CloseWrapper
        editor={editor}
        node={node}
        getPos={getPos}
        loading={Boolean(loading || (pastedFiles?.length && !items.length))}
      >
        {content}
      </CloseWrapper>
    </NodeViewWrapper>
  );
};
